// Scope-agnostic. Both useOrganizationSlackSettings and useUserSlackSettings
// consumers feed this component. Do not introduce scope-aware imports or
// props. See spec §7 resolved Q-C.
import type { ReactElement, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

import { SlackIcon } from '@/components/slack/SlackIcon';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { SlackSettingsResponse } from '@kirin/api-client/contracts/slack-settings';
import { severityLabelMap } from '@kirin/api-client/contracts/shared/severity';

import { IntegrationCard } from './IntegrationCard';
import { renderDefaultActions } from './defaultActions';

export interface SlackIntegrationCardProps {
  settings: SlackSettingsResponse | null | undefined;
  onConnect?: () => void;
  onManage?: () => void;
  onDisconnect?: () => void;
  isLoading?: boolean;
  error?: string;
  readOnly?: boolean;
}

function renderMeta(settings: SlackSettingsResponse): ReactNode {
  return (
    <IntegrationCard.Meta>
      <span data-testid="slack-meta-webhook">Webhook configured</span>
      <span data-testid="slack-meta-min-risk">
        Min risk: {severityLabelMap[settings.minRiskLevel]}
      </span>
    </IntegrationCard.Meta>
  );
}

function renderSlackErrorAlert(error: string | undefined): ReactNode {
  if (!error) return null;
  return (
    <IntegrationCard.Body>
      <Alert data-testid="slack-error-alert" variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    </IntegrationCard.Body>
  );
}

function renderSlackErrorPill(error: string | undefined): ReactNode {
  if (!error) return null;
  return (
    <IntegrationCard.StatusBadge>
      <Badge
        data-testid="slack-error-pill"
        variant="destructive"
        className="gap-1.5"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Error
      </Badge>
    </IntegrationCard.StatusBadge>
  );
}

export function SlackIntegrationCard(
  props: SlackIntegrationCardProps
): ReactElement {
  const {
    settings,
    onConnect,
    onManage,
    onDisconnect,
    isLoading,
    error,
    readOnly,
  } = props;
  const connectedSettings =
    settings != null && settings.enabled === true ? settings : null;
  const connected = connectedSettings !== null;

  return (
    <IntegrationCard
      icon={<SlackIcon className="h-5 w-5" />}
      title="Slack"
      subtitle="Send security alerts to Slack"
      status={connected ? 'connected' : 'not_configured'}
      isLoading={isLoading}
    >
      {renderSlackErrorPill(error)}
      {connectedSettings !== null ? renderMeta(connectedSettings) : null}
      {renderSlackErrorAlert(error)}
      {renderDefaultActions(
        connected,
        { onConnect, onManage, onDisconnect },
        readOnly
      )}
    </IntegrationCard>
  );
}

SlackIntegrationCard.displayName = 'SlackIntegrationCard';
